import { CellMode, CellValue } from '@/shared/ui/data/EditableGrid/types/cell.types';
import { Row } from '@/shared/ui/data/EditableGrid/types/row.types';
import { Column } from '@/shared/ui/data/EditableGrid/types/column.types';
import { useGridState } from '@/shared/ui/data/EditableGrid/useGridState';
import { useCellDiff } from '@/shared/ui/data/EditableGrid/hooks/useCellDiff';
import React, { useCallback, useEffect } from 'react';
import { Position } from '@/shared/ui/data/EditableGrid/types/core.type';
import { useEditCell } from '@/shared/ui/data/EditableGrid/hooks/useEditCell';
import { useHistory } from '@/shared/ui/data/EditableGrid/hooks/useHistory';
import { useKeyboardNav } from '@/shared/ui/data/EditableGrid/hooks/useKeyboardNav';
import { useCellSelection } from '@/shared/ui/data/EditableGrid/hooks/useCellSelection';
import { useDragSelection } from '@/shared/ui/data/EditableGrid/hooks/useDragSelection';
import { useClipboard } from '@/shared/ui/data/EditableGrid/hooks/useClipboard';
import { SelectionMode } from '@/shared/ui/data/EditableGrid/lib/selection';
import { GridToolbar } from '@/shared/ui/data/EditableGrid/GridToolbar';
import { GridContainer } from '@/shared/ui/data/EditableGrid/components/GridContainer';
import { GridRow } from '@/shared/ui/data/EditableGrid/components/GridRow';
import { GridCell } from '@/shared/ui/data/EditableGrid/components/GridCell';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

interface IProps<T = Record<string, CellValue>> {
  rows: ReadonlyArray<Row<T>>;
  prevRows?: ReadonlyArray<Row<T>>;
  columns: ReadonlyArray<Column>;
  enableDiff?: boolean;
  enableUndo?: boolean;
  enableClipboard?: boolean;
  enablePaste?: boolean;
  enableDragSelection?: boolean;
  onCellChange?: (rowId: string, columnId: string, newValue: CellValue) => void;
  /** 행별 추가 스타일 (날짜 그룹 구분 등) */
  getRowSx?: (row: Row<T>, rowIndex: number) => SxProps<Theme>;
}

const MAX_HISTORY_SIZE = 100;

export function EditableGrid<T extends Record<string, CellValue>>(props: IProps<T>) {
  const {
    rows,
    prevRows,
    columns,
    enableDiff = false,
    enableUndo = true,
    enableClipboard = true,
    enablePaste = true,
    enableDragSelection = true,
    onCellChange,
    getRowSx,
  } = props;

  const {
    gridState,
    setFocus,
    setSelection,
    setEditingCell,
    updateCellValue,
    isFocused,
    isEditing,
  } = useGridState({ initialRows: rows, columns, enableDiff });

  ///Diff///
  const cells = useCellDiff({
    currentRows: gridState.rows,
    prevRows,
    columns,
    enableDiff,
  });

  ///History///
  const getCellValue = useCallback(
    (position: Position) => {
      const row = gridState.rows[position.rowIndex];
      const column = columns[position.colIndex];
      if (!row || !column) return null;
      return row.data[column.id] as CellValue;
    },
    [gridState.rows, columns]
  );

  const setCellValueWithNotify = useCallback(
    (position: Position, value: CellValue) => {
      updateCellValue(position, value);
      const row = gridState.rows[position.rowIndex];
      const column = columns[position.colIndex];
      if (row && column) onCellChange?.(row.id, column.id, value);
    },
    [gridState.rows, columns, updateCellValue, onCellChange]
  );

  const history = useHistory({
    onCellValueChange: setCellValueWithNotify,
    onBulkValueChange: (changes) => {
      changes.forEach(({ position, value }) => {
        setCellValueWithNotify(position, value);
      });
    },
    maxHistorySize: MAX_HISTORY_SIZE,
  });

  ///Edit///
  const {
    startEdit,
    saveEdit,
    cancelEdit,
    updateTempValue,
    tempValue,
    handleKeyDown: handleEditKeyDown,
  } = useEditCell({
    onSave: (position, newValue) => {
      const oldValue = getCellValue(position);

      if (enableUndo) history.recordCellEdit(position, oldValue, newValue);

      setCellValueWithNotify(position, newValue);
      setEditingCell(null);
    },
    onCancel: (position) => {
      setEditingCell(null);
    },
    onStartEdit: (position) => {
      setEditingCell(position);
    },
  });

  ///Navigation///
  const { handleNavKeyDown } = useKeyboardNav({
    bound: {
      rowCount: gridState.rows.length,
      colCount: columns.length,
    },
    onMove: (_, to) => {
      setFocus(to);
      setSelection([to]);
    },
  });

  //selection by click
  const { selectCell, selectCellRange, toggleCellSelection, isSelected } = useCellSelection({
    onSelectionChange: (positions) => setSelection(positions),
  });

  //selection by drag
  const dragSelection = useDragSelection({
    onSelectionChange: (positions) => setSelection(positions),
  });

  useEffect(() => {
    if (enableDragSelection) {
      window.addEventListener('mouseup', dragSelection.handleMouseUp);
      return () => window.removeEventListener('mouseup', dragSelection.handleMouseUp);
    }
  }, [enableDragSelection, dragSelection.handleMouseUp]);

  ///Clipboard///
  const clipboard = useClipboard({
    getCellValue,
    setCellValue: (position, value) => {
      const oldValue = getCellValue(position);
      setCellValueWithNotify(position, value);
    },
    bound: {
      rowCount: gridState.rows.length,
      colCount: columns.length,
    },
    columns,
    onPaste: (changes) => {
      if (enableUndo) {
        const bulkChanges = changes.map(({ position, value }) => ({
          position,
          oldValue: getCellValue(position),
          newValue: value,
        }));
        history.recordBulkEdit(bulkChanges);
      }
    },
  });

  const handleCellMouseDown = useCallback(
    (position: Position, e: React.MouseEvent) => {
      if (gridState.editingCell) saveEdit();

      if (enableDragSelection && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        dragSelection.handleMouseDown(position, e);
        setFocus(position);
      }
    },
    [gridState.editingCell, enableDragSelection, dragSelection, setFocus, saveEdit]
  );

  const handleCellClick = useCallback(
    (position: Position, e: React.MouseEvent) => {
      if (gridState.editingCell) saveEdit();

      if (enableDragSelection && !e.shiftKey && !e.ctrlKey && !e.metaKey) return;

      const mode: SelectionMode =
        e.shiftKey && gridState.focusedCell ? 'range' : e.ctrlKey || e.metaKey ? 'multi' : 'single';

      switch (mode) {
        case 'range':
          selectCellRange(position);
          setFocus(position);
          return;
        case 'multi':
          toggleCellSelection(position);
          setFocus(position);
          return;
        case 'single':
          selectCell(position);
          setFocus(position);
          return;
      }
    },
    [
      gridState.editingCell,
      enableDragSelection,
      gridState.focusedCell,
      selectCell,
      selectCellRange,
      toggleCellSelection,
      setFocus,
      saveEdit,
    ]
  );

  const handleCellMouseEnter = useCallback(
    (position: Position) => {
      if (enableDragSelection && dragSelection.isDragging) dragSelection.handleMouseEnter(position);
    },
    [enableDragSelection, dragSelection]
  );

  const handleCellDoubleClick = useCallback(
    (position: Position, initialValue: CellValue) => {
      const column = columns[position.colIndex];
      if (column?.editable === false) return;

      startEdit(position, initialValue);
    },
    [columns, startEdit]
  );

  const handleCellKeyDown = useCallback(
    (e: React.KeyboardEvent, position: Position) => {
      const isEditing = gridState.editingCell !== null;

      if (isEditing) {
        handleEditKeyDown(e, position, columns, cells);
        return;
      }

      if (enableUndo) history.handleKeyDown(e);
      if (enableClipboard)
        clipboard.handleKeyDown(e, gridState.selectedCells, gridState.focusedCell);

      const nextPosition = handleNavKeyDown(e, position);
      if (nextPosition) {
        setFocus(nextPosition);
        return;
      }

      handleEditKeyDown(e, position, columns, cells);
    },
    [
      enableUndo,
      enableClipboard,
      columns,
      cells,
      history,
      clipboard,
      gridState.selectedCells,
      gridState.focusedCell,
      gridState.editingCell,
      handleNavKeyDown,
      handleEditKeyDown,
      setFocus,
    ]
  );

  return (
    <Box
      className="editable-grid-wrapper"
      sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}
    >
      <GridToolbar
        enableUndo={enableUndo}
        canUndo={history.canUndo()}
        canRedo={history.canRedo()}
        onUndo={history.undo}
        onRedo={history.redo}
        enableClipboard={enableClipboard}
        enablePaste={enablePaste}
        selectedCells={gridState.selectedCells}
        focusedCell={gridState.focusedCell}
        isCopying={clipboard.isCopying}
        isPasting={clipboard.isPasting}
        onCopy={() => clipboard.handleCopy(gridState.selectedCells)}
        onPaste={() => {
          if (gridState.focusedCell) clipboard.handlePaste(gridState.focusedCell);
        }}
      />

      <GridContainer columns={columns} allowCopySelect={!onCellChange}>
        <GridRow isHeader>
          {columns.map((col) => (
            <GridCell key={col.id} isHeader>
              {col.label}
            </GridCell>
          ))}
        </GridRow>

        {cells.map((row, rowIndex) => {
          const rowData = gridState.rows[rowIndex];
          const rowSx = getRowSx?.(rowData, rowIndex) ?? {};
          return (
            <GridRow key={rowData.id}>
              {row.map((cell, colIndex) => {
                const position = { rowIndex, colIndex };
                let mode = cell.mode;

                const effectiveSelection = dragSelection.isDragging
                  ? dragSelection.currentSelection
                  : gridState.selectedCells;

                if (
                  gridState.editingCell &&
                  gridState.editingCell.rowIndex === rowIndex &&
                  gridState.editingCell.colIndex === colIndex
                ) {
                  mode = 'editing';
                } else if (
                  gridState.focusedCell &&
                  gridState.focusedCell.rowIndex === rowIndex &&
                  gridState.focusedCell.colIndex === colIndex
                ) {
                  mode = 'focused';
                } else if (
                  effectiveSelection.some((p) => p.rowIndex === rowIndex && p.colIndex === colIndex)
                ) {
                  mode = 'selected';
                }

                const { editing, focused, selected, disabled } = convertCellModeToFlags(mode);

                return (
                  <GridCell
                    key={`${rowIndex}-${colIndex}`}
                    cell={cell}
                    focused={focused}
                    selected={selected}
                    editing={editing}
                    disabled={disabled}
                    rowSx={rowSx}
                    onClick={(e) => handleCellClick(position, e)}
                    onMouseDown={(e) => handleCellMouseDown(position, e)}
                    onMouseEnter={() => handleCellMouseEnter(position)}
                    onDoubleClick={() => handleCellDoubleClick(position, cell.value)}
                    onKeyDown={(e) => handleCellKeyDown(e, position)}
                    onValueChange={editing ? updateTempValue : undefined}
                    onValueCommit={editing ? saveEdit : undefined}
                    tempValue={editing ? tempValue : undefined}
                  />
                );
              })}
            </GridRow>
          );
        })}
      </GridContainer>
    </Box>
  );
}

function convertCellModeToFlags(mode: CellMode): {
  editing: boolean;
  focused: boolean;
  selected: boolean;
  disabled: boolean;
} {
  return {
    editing: mode === 'editing',
    focused: mode === 'focused',
    selected: mode === 'selected',
    disabled: mode === 'disabled',
  };
}
